import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { HoverReveal } from '../components/HoverReveal';
import { DoubleScroll } from '../components/DoubleScroll';
import { StatusBadge } from '../components/StatusBadge';
import { InlineStatusEditor } from '../components/InlineStatusEditor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Trash2,
  Zap,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';
import { ClientLink, empresaUrl } from '../components/client360/c360Shared';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COMPANY_NAMES = {
  edp: 'EDP',
  galp: 'Galp',
  endesa: 'Endesa',
  iberdrola: 'Iberdrola',
  gold: 'Gold Energy',
  repsol: 'Repsol',
  yes: 'Yes',
  meo_energia: 'Meo Energia',
  nos: 'NOS',
  meo: 'MEO',
  vodafone: 'Vodafone',
  nos_securitas: 'NOS Securitas',
  prosegur: 'Prosegur'
};

const CATEGORY_LABELS = {
  luz_gas: 'Luz e Gás',
  solar: 'Solar',
  carregamento: 'Carregamento Elétrico',
  telecomunicacoes: 'Telecomunicações',
  alarmes: 'Alarmes'
};

// Smart tooltip cell: only shows tooltip when text is actually truncated
const TruncCell = ({ children, className = '', mono = false }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const text = typeof children === 'string' ? children : '';

  useEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [children]);

  const baseClass = `truncate block ${mono ? 'text-xs font-mono' : 'text-sm'} ${className}`;

  if (!isTruncated || !text) {
    return <span ref={ref} className={baseClass}>{children}</span>;
  }

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span ref={ref} className={baseClass}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg break-all">
        {text}
      </TooltipContent>
    </Tooltip>
  );
};

// Column min-widths in px - browser distributes remaining space across visible columns
const COL_MIN = {
  checkbox: 36,
  codigo: 55,
  nome_cliente: 170,
  nif: 110,
  tipo_produto: 100,
  tipo_segmento: 110,
  status: 130,
  cpe: 140,
  cui: 140,
  created_at: 105,
  assigned_to_name: 130,
  actions: 36,
};
const colMinStyle = (key) => ({ minWidth: `${COL_MIN[key] || 100}px` });

export const ContractsList = () => {
  const { category, company } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isSuperAdmin, hasPerm } = useAuth();
  
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [statuses, setStatuses] = useState({});
  const [sortKey, setSortKey] = useState(searchParams.get('sort_key') || 'codigo');
  const [sortDir, setSortDir] = useState(searchParams.get('sort_dir') || 'desc');
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('page_size') || '25', 10));
  const [proprietarioFilter, setProprietarioFilter] = useState(searchParams.get('proprietario') || 'all');
  const [partnerFilter, setPartnerFilter] = useState(searchParams.get('partner_id') || 'all');
  const [usersList, setUsersList] = useState([]);
  const [partners, setPartners] = useState([]);

  const companyName = COMPANY_NAMES[company] || company?.toUpperCase();
  const categoryLabel = CATEGORY_LABELS[category] || category;

  // Persist filters/sort/page to URL so back navigation from ContractDetail
  // restores the exact list state the user was viewing.
  useEffect(() => {
    const next = {};
    if (searchQuery) next.q = searchQuery;
    if (statusFilter !== 'all') next.status = statusFilter;
    if (proprietarioFilter !== 'all') next.proprietario = proprietarioFilter;
    if (partnerFilter !== 'all') next.partner_id = partnerFilter;
    if (dateFrom) next.date_from = dateFrom;
    if (dateTo) next.date_to = dateTo;
    if (sortKey && sortKey !== 'codigo') next.sort_key = sortKey;
    if (sortDir && sortDir !== 'desc') next.sort_dir = sortDir;
    if (page > 1) next.page = String(page);
    if (pageSize !== 25) next.page_size = String(pageSize);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, proprietarioFilter, partnerFilter, dateFrom, dateTo, sortKey, sortDir, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [category, company, statusFilter, partnerFilter]);

  // Debounce search → backend matches RAW unmasked data
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchData = async () => {
    try {
      const isAdmin = ['super_admin', 'gestor', 'admin', 'parceiro'].includes(user?.role);
      const params = {};
      // When company is specified, filter by company only (company is unique across categories)
      if (company) params.company = company;
      else if (category) params.category = category;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (partnerFilter !== 'all') params.partner_id = partnerFilter;
      if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();

      const requests = [
        axios.get(`${API_URL}/contracts`, { params }),
        axios.get(`${API_URL}/config/statuses`),
      ];
      if (isAdmin) {
        requests.push(axios.get(`${API_URL}/users`));
        requests.push(axios.get(`${API_URL}/partners`));
      }

      const responses = await Promise.all(requests);
      setContracts(responses[0].data);
      setStatuses(responses[1].data);
      if (isAdmin) {
        if (responses[2]) setUsersList(responses[2].data);
        if (responses[3]) setPartners(responses[3].data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este contrato?')) return;
    
    try {
      await axios.delete(`${API_URL}/contracts/${id}`);
      toast.success('Contrato eliminado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar contrato');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd MMM', { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const filteredContracts = contracts.filter(contract => {
    // NOTE: search is now handled by the backend (matches RAW unmasked data)
    if (dateFrom && contract.created_at && contract.created_at < dateFrom) return false;
    if (dateTo && contract.created_at && contract.created_at > dateTo + 'T23:59:59') return false;
    if (proprietarioFilter !== 'all' && (contract.assigned_to || contract.created_by) !== proprietarioFilter) return false;
    return true;
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedContracts = [...filteredContracts].sort((a, b) => {
    let valA = a[sortKey];
    let valB = b[sortKey];
    if (sortKey === 'codigo') {
      valA = parseInt(valA) || 0;
      valB = parseInt(valB) || 0;
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedContracts.length / pageSize);
  const paginatedContracts = sortedContracts.slice((page - 1) * pageSize, page * pageSize);
  const resetPage = () => setPage(1);

  const SortHeader = ({ colKey, label }) => (
    <TableHead
      className="font-semibold text-xs uppercase cursor-pointer select-none hover:bg-slate-100/80 transition-colors whitespace-nowrap"
      style={colMinStyle(colKey)}
      onClick={() => handleSort(colKey)}
      data-testid={`sort-${colKey}`}
    >
      <div className="flex items-center gap-1 whitespace-nowrap">
        {label}
        {sortKey === colKey ? (
          sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-[#2D3E6F] shrink-0" /> : <ArrowDown className="w-3 h-3 text-[#2D3E6F] shrink-0" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-slate-300 shrink-0" />
        )}
      </div>
    </TableHead>
  );

  return (
    <TooltipProvider>
    <Layout>
      <div className="space-y-6" data-testid="contracts-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <span>{categoryLabel}</span>
              <span>›</span>
              <span className="text-[#4DD4E8]">{companyName}</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Zap className="w-6 h-6 text-[#2D3E6F]" />
              Contratos {companyName}
            </h1>
            <p className="text-slate-500 mt-1">{sortedContracts.length} registos encontrados</p>
          </div>
          {company && company !== 'all' && (
            <Link to={empresaUrl(company)} data-testid="contracts-empresa-360-btn"
              className="ml-auto mr-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-[#2D3E6F] text-[#2D3E6F] hover:bg-[#2D3E6F] hover:text-white transition-colors">
              Resumo {companyName}
            </Link>
          )}
          
          {hasPerm('criar_contratos') && (
          <Button
            onClick={() => navigate(`/contracts/${category}/${company}/new`)}
            className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2"
            data-testid="create-contract-button"
          >
            <Plus className="w-4 h-4" />
            Criar {companyName}
          </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome, NIF ou código..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); resetPage(); }}
                  className="pl-10 h-11 rounded-lg"
                  data-testid="search-contracts-input"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); resetPage(); }}>
                <SelectTrigger className="w-full md:w-56 h-11 rounded-lg" data-testid="status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {statusFilter.includes(',') && <SelectItem value={statusFilter}>Vários estados (do resumo)</SelectItem>}
                  {Object.entries(statuses).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {['super_admin', 'gestor', 'admin', 'parceiro'].includes(user?.role) && partners.length > 0 && (
                <Select value={partnerFilter} onValueChange={(v) => { setPartnerFilter(v); resetPage(); }}>
                  <SelectTrigger className="w-full md:w-48 h-11 rounded-lg" data-testid="partner-filter">
                    <SelectValue placeholder="Parceiro" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os parceiros</SelectItem>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={proprietarioFilter} onValueChange={(v) => { setProprietarioFilter(v); resetPage(); }}>
                <SelectTrigger className="w-full md:w-52 h-11 rounded-lg" data-testid="proprietario-filter">
                  <SelectValue placeholder="Proprietário" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os proprietários</SelectItem>
                  {usersList.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Date range filter */}
            <div className="flex flex-col md:flex-row gap-4 mt-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500 whitespace-nowrap">De:</span>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); resetPage(); }}
                  className="h-9 rounded-lg w-40 text-sm"
                  data-testid="date-from-filter"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 whitespace-nowrap">Até:</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); resetPage(); }}
                  className="h-9 rounded-lg w-40 text-sm"
                  data-testid="date-to-filter"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setDateFrom(''); setDateTo(''); resetPage(); }}
                  className="text-xs text-slate-500 hover:text-slate-700 h-9"
                  data-testid="clear-dates-btn"
                >
                  Limpar datas
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="rounded-2xl border-slate-100">
          <div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            ) : sortedContracts.length === 0 ? (
              <div className="text-center py-16">
                <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-400">Nenhum contrato encontrado</p>
                {hasPerm('criar_contratos') && (
                <Button
                  onClick={() => navigate(`/contracts/${category}/${company}/new`)}
                  variant="outline"
                  className="mt-4"
                >
                  Criar primeiro contrato
                </Button>
                )}
              </div>
            ) : (
              <DoubleScroll>
                <Table className="w-full" style={{ tableLayout: 'auto' }}>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead style={colMinStyle('checkbox')}>
                      <input type="checkbox" className="rounded" />
                    </TableHead>
                    <SortHeader colKey="codigo" label="Codigo" />
                    <SortHeader colKey="nome_cliente" label="Nome Cliente" />
                    <SortHeader colKey="nif" label="NIF" />
                    <SortHeader colKey="tipo_produto" label="Tipo Produto" />
                    <SortHeader colKey="tipo_segmento" label="Segmento" />
                    <SortHeader colKey="status" label="Estado" />
                    <SortHeader colKey="cpe" label="CPE" />
                    <SortHeader colKey="cui" label="CUI" />
                    <SortHeader colKey="created_at" label="Criado em" />
                    <SortHeader colKey="assigned_to_name" label="Proprietario" />
                    <TableHead style={colMinStyle('actions')}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedContracts.map((contract, index) => (
                    <TableRow
                      key={contract.id}
                      className="hover:bg-slate-50/50 cursor-pointer"
                      data-testid={`contract-row-${index}`}
                    >
                      <TableCell>
                        <input type="checkbox" className="rounded" />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="text-sm font-mono">{contract.codigo || '-'}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1 min-w-0">
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            <Link
                              to={`/contracts/${category}/${company}/${contract.id}`}
                              state={{ returnTo: location.pathname + location.search }}
                              className="font-medium text-[#2D3E6F] hover:text-[#4DD4E8] hover:underline truncate block"
                            >
                              {contract.nome_cliente}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                            {contract.nome_cliente}
                          </TooltipContent>
                        </Tooltip>
                        <ClientLink nif={contract.nif} name={contract.nome_cliente} contractId={contract.id} testId={`contract-client-link-${contract.id}`} />
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {(contract.nif || '').includes('*') ? (
                          <TruncCell mono><HoverReveal contractId={contract.id} field="nif">{contract.nif}</HoverReveal></TruncCell>
                        ) : (
                          <TruncCell mono>{contract.nif || '-'}</TruncCell>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <TruncCell>{(contract.tipo_produto || '').replace('_', ' + ')}</TruncCell>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <TruncCell>{contract.tipo_segmento || '-'}</TruncCell>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <InlineStatusEditor
                          contract={contract}
                          onUpdated={(id, newStatus) => {
                            setContracts(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
                          }}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {(contract.cpe || '').includes('*') ? (
                          <TruncCell mono><HoverReveal contractId={contract.id} field="cpe">{contract.cpe}</HoverReveal></TruncCell>
                        ) : (
                          <TruncCell mono>{contract.cpe || '-'}</TruncCell>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {(contract.cui || '').includes('*') ? (
                          <TruncCell mono><HoverReveal contractId={contract.id} field="cui">{contract.cui}</HoverReveal></TruncCell>
                        ) : (
                          <TruncCell mono>{contract.cui || '-'}</TruncCell>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <TruncCell className="text-slate-500">{formatDate(contract.created_at)}</TruncCell>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <TruncCell className="font-medium text-[#4DD4E8]">{contract.assigned_to_name || contract.created_by_name || '-'}</TruncCell>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/contracts/${category}/${company}/${contract.id}`, { state: { returnTo: location.pathname + location.search } })}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalhes
                            </DropdownMenuItem>
                            {isSuperAdmin() && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(contract.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </DoubleScroll>
            )}
          </div>
          
          {/* Pagination */}
          {sortedContracts.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-slate-100 gap-3">
              <div className="flex items-center gap-3">
                <p className="text-sm text-slate-500">
                  {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, sortedContracts.length)} de {sortedContracts.length}
                </p>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8 text-xs" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400">por página</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1} className="h-8 px-2 text-xs" data-testid="page-first">«</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 px-3 text-xs" data-testid="page-prev">Anterior</Button>
                <span className="text-sm text-slate-600 px-3 font-medium">{page} / {totalPages || 1}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="h-8 px-3 text-xs" data-testid="page-next">Seguinte</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="h-8 px-2 text-xs" data-testid="page-last">»</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
    </TooltipProvider>
  );
};
